import { log } from '@/shared/lib/log'

import en from '@/locales/en.json'
import es from '@/locales/es.json'
import pt from '@/locales/pt.json'

import { DEFAULT_LOCALE, type Locale } from './locale'

/**
 * El motor de traducción: catálogo, plurales y variables con nombre.
 *
 * **Por qué un motor propio y no `react-i18next`.** Lo que hace falta acá son
 * tres cosas —buscar una clave en un JSON, elegir la forma plural y sustituir
 * variables con nombre— y las tres partes difíciles ya las trae el navegador:
 * `Intl.PluralRules` es CLDR, la misma tabla que usa `babel` del lado del
 * backend. Una librería agregaría un provider, un ciclo de inicialización
 * asíncrono y un "idioma actual" global a cambio de funcionalidad que no
 * usamos (detección por navegador, backends de carga, namespaces). Este archivo
 * es el espejo de `app/core/i18n.py`, que es lo que hace que una clave
 * signifique lo mismo en los dos lados.
 *
 * Tres garantías, las mismas que el backend:
 *
 * 1. **Una clave que falta no rompe la pantalla.** Cae al idioma por defecto y
 *    deja un `warning` con la clave y el locale. Si tampoco está ahí, devuelve
 *    la clave misma. Una interfaz a medio traducir es fea; una que revienta es
 *    inusable, y el error aparecería justo en el idioma que menos miramos.
 * 2. **Las variables van con nombre**, nunca posicionales. El orden de las
 *    palabras cambia entre idiomas —"vence en 3 días" / "3 days overdue"— y
 *    concatenar pedazos produce frases absurdas apenas alguien traduce. Una
 *    variable que falta deja `{empresa}` a la vista y loguea; no levanta.
 * 3. **Los plurales los decide CLDR**, no un `if n > 1`. En portugués el `0` va
 *    en singular (`0 tarefa vencida`) y en español e inglés en plural.
 */

/** Un nodo del catálogo: o es texto, o es un grupo, o es una entrada con plurales. */
type CatalogNode = string | { [key: string]: CatalogNode }

const CATALOGS: Record<Locale, CatalogNode> = {
  es: es as CatalogNode,
  pt: pt as CatalogNode,
  en: en as CatalogNode,
}

/**
 * Las categorías de plural de CLDR. Un nodo cuyas claves son TODAS de esta
 * lista es una entrada con plurales, no un grupo de claves.
 */
const PLURAL_CATEGORIES = new Set<string>(['zero', 'one', 'two', 'few', 'many', 'other'])

export type TranslateVars = Record<string, string | number>

/** `{nombre}` — marcador con nombre. Nunca `{}` ni `{0}`. */
const PLACEHOLDER = /\{(\w+)\}/g

function isPluralNode(node: CatalogNode): node is Record<string, string> {
  if (typeof node === 'string') return false
  const keys = Object.keys(node)
  return keys.length > 0 && keys.every((key) => PLURAL_CATEGORIES.has(key))
}

/** Baja por el catálogo siguiendo `a.b.c`. `undefined` si no está. */
function lookup(locale: Locale, key: string): CatalogNode | undefined {
  let node: CatalogNode | undefined = CATALOGS[locale]
  for (const part of key.split('.')) {
    if (node === undefined || typeof node === 'string') return undefined
    node = node[part]
  }
  return node
}

const pluralRules = new Map<Locale, Intl.PluralRules>()

function selectPlural(locale: Locale, node: Record<string, string>, count: number): string {
  let rules = pluralRules.get(locale)
  if (!rules) {
    rules = new Intl.PluralRules(locale)
    pluralRules.set(locale, rules)
  }
  const category = rules.select(count)
  // `other` es la única categoría que CLDR garantiza en todo idioma, así que es
  // el respaldo. Si tampoco está, el catálogo está mal y el test lo dice.
  return node[category] ?? node.other ?? Object.values(node)[0] ?? ''
}

/**
 * Sustituye `{nombre}` por su valor.
 *
 * Una variable que no vino se deja **tal cual** y se loguea. Borrarla dejaría
 * una frase muda ("vence en  días") que nadie sabe de dónde salió; dejarla
 * visible hace que el bug se reporte solo.
 */
function interpolate(template: string, vars: TranslateVars, key: string, locale: Locale): string {
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = vars[name]
    if (value === undefined) {
      log.warn('i18n_variable_faltante', { clave: key, locale, variable: name })
      return match
    }
    return String(value)
  })
}

/**
 * La clave `key` en `locale`, con sus variables puestas.
 *
 * `vars.count` tiene un significado especial: si la entrada del catálogo es un
 * objeto de formas plurales, decide cuál se usa. Sigue estando disponible como
 * `{count}` dentro del texto.
 */
export function translate(locale: Locale, key: string, vars: TranslateVars = {}): string {
  let node = lookup(locale, key)
  let effective = locale

  if (node === undefined && locale !== DEFAULT_LOCALE) {
    // Garantía 1: caer al idioma por defecto y avisar. Media pantalla en otro
    // idioma se puede usar; una pantalla en blanco no.
    log.warn('i18n_clave_faltante', { clave: key, locale })
    node = lookup(DEFAULT_LOCALE, key)
    effective = DEFAULT_LOCALE
  }

  if (node === undefined) {
    log.warn('i18n_clave_inexistente', { clave: key, locale })
    return key
  }

  if (isPluralNode(node)) {
    const count = vars.count
    if (typeof count !== 'number') {
      log.warn('i18n_plural_sin_count', { clave: key, locale })
      return key
    }
    return interpolate(selectPlural(effective, node, count), vars, key, effective)
  }

  if (typeof node !== 'string') {
    // Alguien pidió un grupo de claves como si fuera un texto.
    log.warn('i18n_clave_es_un_grupo', { clave: key, locale })
    return key
  }

  return interpolate(node, vars, key, effective)
}

/** Solo para los tests del catálogo: el árbol crudo de cada idioma. */
export function rawCatalog(locale: Locale): CatalogNode {
  return CATALOGS[locale]
}

export { PLURAL_CATEGORIES }
